import { PdfLayout } from '../PdfLayout';
import { ClipboardList, CookingPot, MonitorPlay, BookOpen, BarChart3, CalendarClock } from 'lucide-react';

const benefits = [
  {
    icon: ClipboardList,
    title: 'Work Orders',
    text: 'Create, activate, and complete work orders with part number, target quantity, and priority. Count good and rejected parts at the line.',
  },
  {
    icon: CookingPot,
    title: 'Recipes',
    text: 'Versioned parameter sets per product, written to the machine in one step — with a history of what was applied, when, and for which work order.',
  },
  {
    icon: MonitorPlay,
    title: 'Andon Displays',
    text: 'Full-screen scoreboards for a shop floor TV: running or down, OEE, and good and reject counts for every machine on the line.',
  },
  {
    icon: BookOpen,
    title: 'Work Instructions & Video',
    text: 'Step-by-step operator guides with images, video, or PDFs, and sign-offs recorded with who, when, and which work order.',
  },
  {
    icon: BarChart3,
    title: 'Quality & Downtime Tracking',
    text: 'Log scrap, rework, rejects, and downtime against reason codes at the machine, then rank the causes on Pareto charts.',
  },
  {
    icon: CalendarClock,
    title: 'Shift Scheduling',
    text: 'Define shifts, breaks, and holidays on a calendar. OEE and andon counts follow the shift that is running.',
  },
];

export function PremadeSetups() {
  return (
    <PdfLayout
      category="Ready-Made Modules"
      headline="Production-Ready"
      headlineHighlight="Out of the Box"
      description="Scada.Ink includes ready-made modules for the most common shop floor needs — work orders, recipes, andon boards, video work instructions, quality and downtime tracking, and shift scheduling. Switch them on and configure them; there is nothing to code."
      accentColor="#7c3aed"
      benefits={benefits}
    >
      <SetupsMockup />
    </PdfLayout>
  );
}

function SetupsMockup() {
  return (
    <div className="glass-panel p-3 w-full max-w-[280px]">
      <div className="flex items-center gap-2 mb-2 px-1">
        <div className="flex gap-1.5">
          <div className="w-2 h-2 rounded-full bg-status-fault/60" />
          <div className="w-2 h-2 rounded-full bg-status-idle/60" />
          <div className="w-2 h-2 rounded-full bg-status-running/60" />
        </div>
        <span className="text-[9px] text-text-muted">Production Modules</span>
      </div>

      {/* Module tiles */}
      <div className="grid grid-cols-2 gap-1.5">
        {[
          { label: 'Work Orders', icon: '📋', count: '3 active', color: '#3b82f6' },
          { label: 'Recipes', icon: '⚙️', count: '12 saved', color: '#7c3aed' },
          { label: 'Andon Board', icon: '🖥️', count: 'Live', color: '#16a34a' },
          { label: 'Instructions', icon: '📖', count: '8 guides', color: '#06b6d4' },
          { label: 'Quality Log', icon: '✓', count: 'Today: 14 rejects', color: '#ca8a04' },
          { label: 'Shift Schedule', icon: '🗓️', count: 'Day shift', color: '#dc2626' },
        ].map((m) => (
          <div key={m.label} className="glass-card p-2">
            <div className="flex items-center gap-1.5 mb-1">
              <span className="text-[10px]">{m.icon}</span>
              <span className="text-[8px] font-semibold text-text-primary">{m.label}</span>
            </div>
            <div className="text-[7px] text-text-muted">{m.count}</div>
            <div className="mt-1 h-1 rounded-full overflow-hidden bg-glass-border">
              <div className="h-full rounded-full" style={{ width: '100%', backgroundColor: m.color, opacity: 0.5 }} />
            </div>
          </div>
        ))}
      </div>

      {/* Andon preview strip */}
      <div className="glass-card p-2 mt-1.5">
        <span className="text-[7px] text-text-muted block mb-1">Andon — Line 1</span>
        <div className="flex gap-1">
          {[
            { name: 'CNC-01', status: 'running' },
            { name: 'CNC-02', status: 'running' },
            { name: 'CNC-03', status: 'running' },
            { name: 'CNC-04', status: 'fault' },
            { name: 'CNC-05', status: 'running' },
          ].map((m) => (
            <div key={m.name} className="flex-1 text-center">
              <div
                className="w-full h-3 rounded-sm mb-0.5"
                style={{
                  backgroundColor:
                    m.status === 'running' ? '#16a34a' : '#dc2626',
                  opacity: 0.7,
                }}
              />
              <span className="text-[5px] text-text-muted">{m.name}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Work order preview */}
      <div className="glass-card p-2 mt-1.5">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[7px] text-text-muted">Active Work Order</span>
          <span className="text-[6px] font-medium px-1 py-0.5 rounded-full" style={{ backgroundColor: '#3b82f612', color: '#3b82f6' }}>
            WO-2471
          </span>
        </div>
        <div className="text-[8px] font-semibold text-text-primary">Bracket Assembly — Lot 47</div>
        <div className="flex items-center gap-2 mt-1">
          <div className="flex-1 h-1.5 bg-glass-border rounded-full overflow-hidden">
            <div className="h-full rounded-full" style={{ width: '72%', backgroundColor: '#3b82f6' }} />
          </div>
          <span className="text-[6px] text-text-muted">72%</span>
        </div>
      </div>

      <div className="flex items-center justify-between mt-2 px-1">
        <span className="text-[8px] text-text-muted">6 modules enabled</span>
        <span className="text-[8px] text-brand-secondary font-medium">Feature Toggles</span>
      </div>
    </div>
  );
}
